import React, { useEffect, useState } from 'react';
import styles from '../../src/assets/scss/AddRoles.module.scss';
import Pagination from '../Components/Common/Pagination';
import { FaTrash } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import DeleteConfirmModal from '../Components/Common/DeleteConfirmModal';
import { useGetPermissionQuery, useCreateroleMutation, useUpdateRoleMutation } from '../slices/admin/admin';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import 'react-datepicker/dist/react-datepicker.css';

const AddRoles = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedPermissionIndex, setSelectedPermissionIndex] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState('');
  const rowsPerPage = 10;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'Active',
  });

  const [mode, setMode] = useState('add');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const { data: permissionData } = useGetPermissionQuery({
    offset: 1,
    limit: 100,
    isActive: true,
    search: '',
  });

  const [createRole] = useCreateroleMutation();
  const [updateRole] = useUpdateRoleMutation();


  const allPermissions = permissionData?.result || [];

  useEffect(() => {
    if (location.state?.mode === 'edit') {
      setMode('edit');
      const roleData = location.state.data;

      setFormData({
        roleName: roleData.roleName || '',
        description: roleData.description || '',
        status: roleData.isActive ? 'Active' : 'In Active',
      });

      if (roleData.effectiveDate) {
        const formatted = dayjs(roleData.effectiveDate).format('YYYY-MM-DD');
        setEffectiveDate(formatted);
      }

      if (Array.isArray(roleData.permissions)) {
        const mapped = roleData.permissions.map((id) => {
          const permissionObj = allPermissions.find((p) => p._id === id);
          return {
            permissionId: id,
            permissionName: permissionObj?.permissionName || '',
            description: permissionObj?.description || '',
          };
        });
        setSelectedPermissions(mapped);
      }
    }
  }, [location, allPermissions]);


  const handleCancel = () => navigate(-1);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // const handleAddPermission = () => {
  //   setSelectedPermissions(prev => [
  //     ...prev,
  //     { permissionId: '', permissionName: '', description: '' }
  //   ]);
  // };

  const handleAddPermission = () => {
    setSelectedPermissions(prev => [
      { permissionId: '', permissionName: '', description: '' },
      ...prev
    ]);
    setCurrentPage(1);
  };



  const handlePermissionChange = (index, value) => {
    const selected = allPermissions.find((p) => p._id === value);
    const updated = [...selectedPermissions];
    updated[index] = {
      permissionId: selected?._id || '',
      permissionName: selected?.permissionName || '',
      description: selected?.description || '',
    };
    setSelectedPermissions(updated);
  };

  const handleDeleteClick = (index) => {
    setSelectedPermissionIndex(index);
    setModalOpen(true);
  };

  const confirmDelete = () => {
    const updated = [...selectedPermissions];
    updated.splice(selectedPermissionIndex, 1);
    setSelectedPermissions(updated);
    setSelectedPermissionIndex(null);
    setModalOpen(false);
  };


  const handleSubmit = async () => {
    const payload = {
      roleName: formData.roleName || formData.name,
      description: formData.description,
      isActive: formData.status === 'Active',
      permissions: selectedPermissions.map((p) => p.permissionId),
      effectiveDate: effectiveDate,
    };

    try {
      if (mode === 'edit') {
        const id = location.state.data._id;
        await updateRole({ id, data: payload }).unwrap();
        toast.success('Role updated successfully');
      } else {
        await createRole(payload).unwrap();
        toast.success('Role created successfully');
      }
      navigate('/dashboard/Role');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to save role');
    }
  };


  const totalPages = Math.ceil(selectedPermissions.length / rowsPerPage);
  const paginatedPermissions = selectedPermissions.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.tittle}>{mode === 'edit' ? 'Update Role' : 'Add Role'}</h2>
        <span className={styles.closeIcon} onClick={handleCancel}>✕</span>
      </div>

      <div className={styles.formContainer}>
        <div className={styles.formGrid}>
          <div>
            <label className={styles.inputLabel}>Role Name</label>
            <input
              type="text"
              name="roleName"
              placeholder="Role Name"
              className={styles.input}
              value={formData.roleName}
              onChange={handleChange}
            />
          </div>
          <div>
            <label className={styles.inputLabel}>Role Description</label>
            <input
              type="text"
              name="description"
              placeholder="Role Description"
              className={styles.input}
              value={formData.description}
              onChange={handleChange}
            />
          </div>
          <div>
            <label className={styles.inputLabel}>Effective Date</label>

            <DatePicker
              selected={effectiveDate ? dayjs(effectiveDate, 'YYYY-MM-DD').toDate() : null}
              onChange={(date) => {
                const formatted = dayjs(date).format('YYYY-MM-DD');
                setEffectiveDate(formatted);
              }}
              dateFormat="MM.dd.yyyy"
              minDate={new Date()}
              placeholderText="Select date"
              className={styles.input}
              wrapperClassName={styles.fullWidthDatepicker}
            />


          </div>
          <div>
            <label className={styles.inputLabel}>Status</label>

            <select
              name="status"
              className={styles.inputs}
              value={formData.status}
              onChange={handleChange}
            >
              <option value="Active">Active</option>
              <option value="In Active">In Active</option>
            </select>

          </div>
        </div>

        <div className={styles.footerButtons}>
          <button className={styles.saves} type="button" onClick={handleAddPermission}>
            + Add Permission
          </button>
        </div>

        <div className={styles.tableWrapper}>
          <div className={`${styles.tableRow} ${styles.headerRow}`}>
            <div className={styles.checkbox}><input type="checkbox" /></div>
            <div>Permission Seq No</div>
            <div>Permission Name</div>
            <div>Description</div>
            <div></div>
          </div>

          {selectedPermissions.length === 0 ? (
            <div className={styles.noData}>No permissions added yet</div>
          ) : (
            // paginatedPermissions.map((perm, index) => (
            //   <div className={styles.tableRow} key={index}>
            //     <div className={styles.checkbox}><input type="checkbox" /></div>

            //     {/* Serial number */}
            //     <div>{(currentPage - 1) * rowsPerPage + index + 1}</div>

            //     <div>
            //       <select
            //         className={styles.inputs}
            //         value={perm.permissionId}
            //         onChange={(e) => handlePermissionChange(index, e.target.value)}
            //       >
            //         <option value="">Select Permission</option>
            //         {allPermissions.map((p) => (
            //           <option key={p._id} value={p._id}>{p.permissionName}</option>
            //         ))}
            //       </select>
            //     </div>

            //     <div>{perm.description || '—'}</div>

            //     <div>
            //       <FaTrash className={styles.icon} onClick={() => handleDeleteClick(index)} />
            //     </div>
            //   </div>
            // ))
            paginatedPermissions.map((perm, index) => (
              <div className={styles.tableRow} key={index}>
                <div className={styles.checkbox}><input type="checkbox" /></div>

                {/* Serial number */}
                <div>{(currentPage - 1) * rowsPerPage + index + 1}</div>

                <div>
                  <select
                    className={styles.inputs}
                    value={perm.permissionId}
                    // ❌ Wrong: this only updates current page index
                    // onChange={(e) => handlePermissionChange(index, e.target.value)}

                    // ✅ Fix: pass global index
                    onChange={(e) =>
                      handlePermissionChange((currentPage - 1) * rowsPerPage + index, e.target.value)
                    }
                  >
                    <option value="">Select Permission</option>
                    {allPermissions.map((p) => (
                      <option key={p._id} value={p._id}>{p.permissionName}</option>
                    ))}
                  </select>
                </div>

                <div>{perm.description || '—'}</div>

                <div>
                  <FaTrash
                    className={styles.icon}
                    onClick={() =>
                      handleDeleteClick((currentPage - 1) * rowsPerPage + index) // ✅ also fix delete
                    }
                  />
                </div>
              </div>
            ))

          )}
        </div>


        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />

        <div className={styles.buttonGroup}>
          <button className={styles.cancel} onClick={handleCancel}>Cancel</button>
          <button className={styles.save} onClick={handleSubmit}>
            {mode === 'edit' ? 'Update' : 'Save'}
          </button>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default AddRoles;



